// SurveyDataContext.js
import React, { createContext, useContext, useEffect, useState } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { useToasts } from "react-toast-notifications";

// Action (from the snippet)
import {
  SG_FETCH_TASK_SCHEDULE,
  SG_ADD_USER_TASK,
  SG_DELETE_USER_TASK,
  SG_UPDATE_EMAIL_TEMPLATE,
  SG_ADD_EMPLOYEE_REPORT_ACCESS,
  SG_REMOVE_EMPLOYEE_REPORT_ACCESS,
  SG_UPDATE_PULSE,
  SG_UPDATE_TASK_TEMPLATE,
} from "constants/actions";

// Utils
const getRecentResponses = (data, employees, filters, get_employee_categories) => {
  const responses = {};
  const employeeList = employees?.employees;

  // Filter the employee categories based on the selected filters
  const filteredEmployeeCategories = get_employee_categories?.filter((employeeCategory) => {
    // If no filters are set, include all categories
    if (!filters || filters.length === 0) {
      return true;
    }

    // Check if the employee category matches any of the selected filters
    return filters.every(filter => {
      // Get the matching category from employee's categories
      const matchingCategory = employeeCategory.category.find(cat =>
        cat.value_id === filter.id
      );

      // If filter only specifies a category name, just check if category exists
      return !!matchingCategory;
    });
  })?.map((employeeCategory) => employeeCategory.employee_id);

  data.task_responses.forEach((response) => {
    const employeeId = Number(response.employee);
    // Find the employee's name, and add it to the response object under the key "employee_full_name"
    // Keep the most recent response for each employee
    const employeeObj = employeeList?.find((emp) => emp.id === employeeId);
    response.employee_full_name = `${employeeObj?.first_name} ${employeeObj?.last_name}`;
    if (
      !responses[employeeId] ||
      response.created_at > responses[employeeId].created_at
    ) {
      responses[employeeId] = response;
    }
  });

  // Filter the responses based on the filteredEmployeeCategories
  const filteredResponses = Object.values(responses).filter((response) => {
    if (filters.length === 0) {
      return true;
    }

    // Check if the response.employee is in the filteredEmployeeCategories array
    return filteredEmployeeCategories.includes(response.employee);
  });

  return filteredResponses;
};

// Calculate compliance percentage
const calculateCompliance = (participants, responses) => {
  if (!participants || participants.length === 0) {
    return 0;
  }

  // Get unique participant IDs
  const participantIds = participants.map(p => p.id);

  // Get unique response employee IDs
  const responseEmployeeIds = responses.map(r => Number(r.employee));

  // Count how many participants have responded
  const respondedCount = participantIds.filter(id =>
    responseEmployeeIds.includes(id)
  ).length;

  // Calculate percentage
  const compliancePercentage = Math.round((respondedCount / participantIds.length) * 100);

  return {
    percentage: compliancePercentage,
    respondedCount,
    totalParticipants: participantIds.length
  };
};

// Create the context
const SurveyDataContext = createContext(null);

// Custom Hook to use the context
export const useSurveyDataContext = () => useContext(SurveyDataContext);

// Provider component
export const SurveyDataProvider = ({ children }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const { addToast } = useToasts();

  const [responses, setResponses] = useState([]);
  const [downloadableReports, setDownloadableReports] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isSurveyCreator, setIsSurveyCreator] = useState(false);
  const [surveyParticipants, setSurveyParticipants] = useState([]);
  const [surveyIsAnonymous, setSurveyIsAnonymous] = useState(false);
  const [compliance, setCompliance] = useState({
    percentage: 0,
    respondedCount: 0,
    totalParticipants: 0
  });

  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [hasEmailChanged, setHasEmailChanged] = useState(false);
  const [pendingUserAssignment, setPendingUserAssignment] = useState(null);
  const [newEmailSubject, setNewEmailSubject] = useState("");
  const [newEmailBody, setNewEmailBody] = useState("");

  const [surveyInfo, setSurveyInfo] = useState({
    title: "",
    description: "",
    createdBy: "",
    createdDate: "",
    updatedDate: "",
    surveyIsAnonymous: false,
    surveyIsOpen: true,
    task_template_id: null,
  });

  // Redux Data
  const {
    fetched_scheduled_task,
    get_employees,
    get_auth,
    pendingReport,
    updated_email_template,
    filters,
    get_employee_categories,
  } = useSelector(
    (state) => ({
      fetched_scheduled_task: state.task_templates?.fetched_scheduled_task,
      get_employees: state.employees,
      get_auth: state.auth,
      pendingReport: state.task_templates?.pendingReport,
      updated_email_template: state.email_templates.updated_email_template,
      filters: state.audit?.new_filters,
      get_employee_categories: state.employee_category,
    }),
    shallowEqual
  );

  useEffect(() => {
    if (fetched_scheduled_task) {
      const emailTemplate = fetched_scheduled_task?.task_template_email;
      setEmailSubject(emailTemplate?.email_subject || "");
      setEmailBody(emailTemplate?.email_content || "");

      // Update survey info when task is fetched
      setSurveyInfo({
        title: fetched_scheduled_task?.task_template_user_tasks?.name || fetched_scheduled_task?.name || "",
        description: fetched_scheduled_task?.description || "",
        createdBy: fetched_scheduled_task?.created_by?.name || "",
        createdDate: fetched_scheduled_task?.created_at || "",
        updatedDate: fetched_scheduled_task?.updated_at || "",
        surveyIsAnonymous: fetched_scheduled_task?.is_anonymous || false,
        surveyIsOpen: fetched_scheduled_task?.status !== 51,
        task_template_id: fetched_scheduled_task?.task_template_user_tasks?.id || null,
      });
    }
  }, [fetched_scheduled_task]);


  // 1) Add user(s) to Report Access
  const addEmployeeReportAccess = async (task, employeeIdList) => {
    if (!employeeIdList || employeeIdList.length === 0) return;
    try {
      // Dispatch the Redux action
      await dispatch({
        type: SG_ADD_EMPLOYEE_REPORT_ACCESS,
        payload: {
          task_id: Number(task),
          employee_id_list: employeeIdList,
        },
      });
      addToast("User(s) successfully added to the report.", {
        appearance: "success",
        autoDismiss: true,
      });
    } catch (error) {
      addToast("An error occurred while adding user(s) to the report.", {
        appearance: "error",
        autoDismiss: true,
      });
    }
  };

  // 2) Remove user(s) from Report Access
  const removeEmployeeReportAccess = async (id, employeeIdList) => {
    if (!employeeIdList || employeeIdList.length === 0) return;
    try {
      // Dispatch the Redux action
      await dispatch({
        type: SG_REMOVE_EMPLOYEE_REPORT_ACCESS,
        payload: {
          task_id: Number(id),
          employee_id_list: employeeIdList,
        },
      });
      addToast("User access removed from the report.", {
        appearance: "success",
        autoDismiss: true,
      });
    } catch (error) {
      addToast("An error occurred while removing user access.", {
        appearance: "error",
        autoDismiss: true,
      });
    }
  };

  const saveChangesAndAssignUsers = async (templateId, employeesList) => {
    try {
      let emailTaskId = fetched_scheduled_task?.task_template_email?.id;

      if (hasEmailChanged) {
        const emailPayload = {
          email_subject: newEmailSubject,
          email_content: newEmailBody,
          task_template: templateId,
          id: emailTaskId,
        };

        // Update Email Template
        await dispatch({
          type: SG_UPDATE_EMAIL_TEMPLATE,
          payload: emailPayload,
        });
        setPendingUserAssignment({ templateId, employeesList });
        addToast("Updating email template...", {
          appearance: "info",
          autoDismiss: true,
        });
      } else {
        // Directly assign users if no email update is needed
        assignUsersToTask(templateId, employeesList);
      }
    } catch (error) {
      addToast("An error occurred while saving changes.", {
        appearance: "error",
        autoDismiss: true,
      });
    }
  };

  const assignUsersToTask = async (templateId, employeesList) => {
    try {
      const userTaskPayload = {
        task_id: templateId,
        employee_id_list: employeesList,
      };

      await dispatch({ type: SG_ADD_USER_TASK, payload: userTaskPayload });
      addToast("Users assigned successfully.", {
        appearance: "success",
        autoDismiss: true,
      });
      setPendingUserAssignment(null); // Clear pending assignment
    } catch (error) {
      addToast("An error occurred while assigning users.", {
        appearance: "error",
        autoDismiss: true,
      });
    }
  };

  useEffect(() => {
    // Trigger user assignment after email update
    if (updated_email_template && pendingUserAssignment) {
      const { templateId, employeesList } = pendingUserAssignment;
      assignUsersToTask(templateId, employeesList);
    }
  }, [updated_email_template]);

  const handleEmailUpdate = (subject, body) => {
    if (subject !== emailSubject || body !== emailBody) {
      setHasEmailChanged(true);
      setNewEmailSubject(subject);
      setNewEmailBody(body);
    }
  };

  const fetchSurvey = (id) => {
    dispatch({ type: SG_FETCH_TASK_SCHEDULE, payload: id });
  };

  const deleteEmployeeFromSurvey = (templateId, employee) => {
    if (employee) {
      const structurePayload = {
        task_id: Number(templateId),
        employee_id_list: [employee],
      };
      dispatch({ type: SG_DELETE_USER_TASK, payload: structurePayload });

      // Update the survey participants
      setSurveyParticipants(surveyParticipants.filter(participant => participant.id !== employee));

      // Add toast message
      addToast("Employee removed from survey", {
        appearance: "success",
        autoDismiss: true,
      });
    } else {
      addToast("Please select an employee to remove", {
        appearance: "error",
        autoDismiss: true,
      });
    }
  };

  const handleArchiveSurvey = (templateId) => {
    dispatch({ type: SG_UPDATE_TASK_TEMPLATE, payload: { id: templateId, status: 99 } });
    setSurveyInfo({
      ...surveyInfo,
      surveyIsOpen: false,
    });
    addToast("Survey archived", {
      appearance: "success",
      autoDismiss: true,
    });
  }

  const handleCloseSurvey = (templateId) => {
    dispatch({ type: SG_UPDATE_PULSE, payload: { id: templateId, status: 51 } });
    setSurveyInfo({
      ...surveyInfo,
      surveyIsOpen: false,
    });
    addToast("Survey closed", {
      appearance: "success",
      autoDismiss: true,
    });
  };


  // Process the responses once the scheduled_task is fetched
  useEffect(() => {
    if (fetched_scheduled_task?.task_responses && get_employees?.employees) {
      const recent = getRecentResponses(fetched_scheduled_task, get_employees, filters, get_employee_categories?.employee_org_category);
      const surveyTitle = fetched_scheduled_task?.task_template_user_tasks?.name || fetched_scheduled_task?.name || "";
      // Format the date as Aug 28, 2024
      const createdDate = new Date(
        fetched_scheduled_task.created_at
      ).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
      const updatedDate = new Date(
        fetched_scheduled_task.updated_at
      ).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
      const createdBy =
        fetched_scheduled_task?.task_template_user_tasks?.user_tasks?.find(
          (task) => task.task_type === "created_by"
        )?.name;
      const description =
        fetched_scheduled_task?.task_template_user_tasks?.user_tasks?.find(
          (task) => task.task_type === "description"
        )?.text;

      const participants = Object?.values(
        fetched_scheduled_task?.user_requests?.user_requests || {}
      );

      // Check fetched_scheduled_task?.assigned_employees to see if any are missing from participants array
      // if so create a new participant object with the employee_full_name
      const missingParticipants = fetched_scheduled_task?.assigned_employees?.filter(employee => !participants.some(participant => participant.id === employee));
      // Get the employee objects for the missing participants
      const missingParticipantsObjects = get_employees?.employees?.filter(employee => missingParticipants.includes(employee.id));
      // Add the missing participants to the participants array
      missingParticipantsObjects.forEach(employee => {
        participants.push({ id: employee.id, email: employee.email, email_sent: false, first_name: employee.first_name, status: "Pending" });
      });

      const questions =
        fetched_scheduled_task?.task_template_user_tasks?.user_tasks?.filter(
          (task) => task.task_type === "question"
        );

      const downloadReports = fetched_scheduled_task?.task_reports;
      if (Object.keys(pendingReport)?.length > 0) {
        downloadReports.push(pendingReport);
      }

      const isAnon =
        fetched_scheduled_task?.task_template_user_tasks?.user_tasks?.find(
          (task) => task.task_type === "survey_is_anonymous"
        )?.value;

      setSurveyIsAnonymous(isAnon);
      setDownloadableReports(downloadReports);
      setQuestions(questions);

      // Loop through participants and add the employee_full_name
      participants.forEach((participant) => {
        const employeeObj = get_employees?.employees?.find(
          (emp) => emp.id === participant.id
        );
        participant.employee_full_name = `${employeeObj?.first_name} ${employeeObj?.last_name}`;
      });

      setSurveyParticipants(participants);
      setSurveyInfo({
        title: surveyTitle,
        description: description,
        createdBy: createdBy,
        createdDate: createdDate,
        updatedDate: updatedDate,
        surveyIsAnonymous: isAnon,
        surveyIsOpen: fetched_scheduled_task?.status === 1,
        task_template_id: fetched_scheduled_task?.task_template_user_tasks?.id || null,
      });

      setResponses(recent);

      // Calculate and set compliance
      const complianceData = calculateCompliance(participants, recent);
      setCompliance(complianceData);
    }
  }, [fetched_scheduled_task, get_employees, pendingReport, filters]);

  // Determine if the current user created this survey
  useEffect(() => {
    if (Number(get_auth?.employee_id) === fetched_scheduled_task?.created_by) {
      setIsSurveyCreator(true);
    }
  }, [fetched_scheduled_task, get_auth]);

  // A callback for selecting a user & navigating to "Individual" tab
  const handleUserSelect = (user) => {
    setSelectedUser(user);
  };

  // A callback to handle returning to the main survey list
  const goBack = () => {
    navigate("/leader/audit/AI/pulse");
  };

  const updateSurveyName = async (newName, taskTemplateId) => {
    try {
      // Update the name of the task template within the user_tasks array
      const updatedUserTasks = fetched_scheduled_task?.task_template_user_tasks?.user_tasks?.map(task => {
        if (task.task_type === "name") {
          return { ...task, text: newName };
        }
        return task;
      });
      const payload = {
        id: taskTemplateId,
        name: newName,
        task_template_user_tasks: {
          ...fetched_scheduled_task?.task_template_user_tasks,
          user_tasks: updatedUserTasks
        }
      };

      await dispatch({
        type: SG_UPDATE_TASK_TEMPLATE,
        payload: payload
      });
      setSurveyInfo(prev => ({
        ...prev,
        title: newName
      }));
      addToast("Survey name updated successfully", {
        appearance: "success",
        autoDismiss: true,
      });
    } catch (error) {
      addToast("Failed to update survey name", {
        appearance: "error",
        autoDismiss: true,
      });
    }
  };

  const updateSurveyDescription = async (newDescription, taskTemplateId) => {
    try {
      // Description is found in the user_tasks array
      const descriptionTask = fetched_scheduled_task?.task_template_user_tasks?.user_tasks?.find(task => task.task_type === "description");
      if (!descriptionTask) {
        throw new Error("Description task not found");
      }
      // Update the text property of the description task
      descriptionTask.text = newDescription;

      // Create the payload, Preserve the other user_tasks, only update the description, leave the rest of the user_tasks the same
      // Loop through the user_tasks array and create a new array with the updated description
      const updatedUserTasks = fetched_scheduled_task?.task_template_user_tasks?.user_tasks?.map(task => {
        if (task.task_type === "description") {
          return { ...task, text: newDescription };
        }
        return task;
      });
      const payload = {
        id: taskTemplateId,
        task_template_user_tasks: {
          ...fetched_scheduled_task?.task_template_user_tasks,
          user_tasks: updatedUserTasks
        }
      };
      await dispatch({
        type: SG_UPDATE_TASK_TEMPLATE,
        payload: payload
      });
      setSurveyInfo(prev => ({
        ...prev,
        description: newDescription
      }));
      addToast("Survey description updated successfully", {
        appearance: "success",
        autoDismiss: true,
      });
    } catch (error) {
      addToast("Failed to update survey description", {
        appearance: "error",
        autoDismiss: true,
      });
    }
  };

  // We'll expose these as context values
  return (
    <SurveyDataContext.Provider
      value={{
        id,
        fetched_scheduled_task,
        get_employees,
        responses,
        selectedUser,
        setSelectedUser,
        handleUserSelect,
        isSurveyCreator,
        goBack,
        fetchSurvey,
        surveyInfo,
        surveyParticipants,
        questions,
        downloadableReports,
        deleteEmployeeFromSurvey,
        surveyIsAnonymous,
        handleEmailUpdate,
        emailSubject,
        emailBody,
        saveChangesAndAssignUsers,
        addEmployeeReportAccess,
        removeEmployeeReportAccess,
        handleCloseSurvey,
        updateSurveyName,
        updateSurveyDescription,
        handleArchiveSurvey,
        compliance,
      }}
    >
      {children}
    </SurveyDataContext.Provider>
  );
};
